import { mergeArticleDetail } from "./lib/data-client.mjs";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Sidebar } from "./components/Sidebar";
import { NewsFeed } from "./components/NewsFeed";
import { ArticleReader } from "./components/ArticleReader";
import { KortNyt } from "./components/KortNyt";
import { BreakingNewsBanner } from "./components/BreakingNewsBanner";
import { SearchBar } from "./components/SearchBar";
import { ArrowLeft, Menu, X, Zap } from "lucide-react";
import { useArticleAPI } from "./hooks/useNewsData";
import { useKortNytData } from "./hooks/useKortNytData";
import { useTheme } from "./hooks/useTheme";
import { useSearch, filterArticles, sortByPublishedDesc } from "./hooks/useSearch";

function App() {
  const [activeArticleMeta, setActiveArticleMeta] = useState(null);
  const [activeArticleDetail, setActiveArticleDetail] = useState(null);
  const [activeSource, setActiveSource] = useState("all");
  const [newArticleIds, setNewArticleIds] = useState(new Set());
  const [mobileFeedOpen, setMobileFeedOpen] = useState(false);
  const [mobileView, setMobileView] = useState("feed");
  const detailRequestRef = useRef(0);
  const newArticlesTimerRef = useRef(null);

  const {
    articles: articleData,
    stats,
    loading,
    fetchArticleDetail,
    searchArticles,
    fetchError,
    lastUpdated,
  } = useArticleAPI();
  const articles = useMemo(() => sortByPublishedDesc(articleData), [articleData]);
  const { query: searchQuery, results: searchResults, handleSearch } = useSearch(searchArticles);
  const { items: kortNytItems, breaking: breakingItems, loading: kortNytLoading } = useKortNytData();
  const dataError = fetchError;
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const handleNewArticles = (event) => {
      setNewArticleIds(event.detail?.ids || new Set());
      if (newArticlesTimerRef.current) clearTimeout(newArticlesTimerRef.current);
      newArticlesTimerRef.current = setTimeout(() => setNewArticleIds(new Set()), 2500);
    };
    window.addEventListener("new-articles", handleNewArticles);
    return () => {
      window.removeEventListener("new-articles", handleNewArticles);
      if (newArticlesTimerRef.current) clearTimeout(newArticlesTimerRef.current);
    };
  }, []);

  const activeArticle = useMemo(
    () => activeArticleMeta
      ? mergeArticleDetail(activeArticleMeta, activeArticleDetail)
      : null,
    [activeArticleMeta, activeArticleDetail]
  );

  useEffect(() => {
    setActiveArticleMeta((current) => {
      if (!current) return current;
      return articles.find((article) => article.id === current.id) || current;
    });
  }, [articles]);

  const filteredArticles = searchResults !== null
    ? searchResults
    : filterArticles(articles, activeSource);

  const closeMobile = useCallback(() => setMobileFeedOpen(false), []);

  const loadArticleDetail = useCallback((article) => {
    const requestId = ++detailRequestRef.current;
    setActiveArticleDetail(null);
    fetchArticleDetail(article).then((detail) => {
      if (detailRequestRef.current === requestId) setActiveArticleDetail(detail);
    });
  }, [fetchArticleDetail]);

  useEffect(() => {
    if (activeArticleMeta) loadArticleDetail(activeArticleMeta);
  }, [activeArticleMeta, loadArticleDetail]);

  const selectArticle = useCallback((article) => {
    setActiveArticleMeta(article);
    setMobileView("article");
    setMobileFeedOpen(false);
    window.history.pushState({ articleId: article.id }, "", `?article=${article.id}`);
  }, []);

  const goBackToFeed = useCallback(() => {
    detailRequestRef.current += 1;
    setActiveArticleMeta(null);
    setActiveArticleDetail(null);
    setMobileView("feed");
    window.history.pushState({}, "", window.location.pathname);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;

      if (e.key === "Escape") {
        if (mobileView === "article") goBackToFeed();
        else if (mobileView === "kort") setMobileView("feed");
        else if (mobileFeedOpen) closeMobile();
        return;
      }

      if (!activeArticle) return;

      const currentIndex = filteredArticles.findIndex((a) => a.id === activeArticle.id);
      if (e.key === "ArrowDown" || e.key === "j") {
        e.preventDefault();
        const nextIndex = currentIndex + 1;
        if (nextIndex >= 0 && nextIndex < filteredArticles.length) {
          selectArticle(filteredArticles[nextIndex]);
        }
      } else if (e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault();
        const prevIndex = currentIndex - 1;
        if (prevIndex >= 0 && prevIndex < filteredArticles.length) {
          selectArticle(filteredArticles[prevIndex]);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeArticle, filteredArticles, mobileView, mobileFeedOpen, selectArticle, goBackToFeed, closeMobile]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const articleId = params.get("article");
    if (articleId && articles.length > 0) {
      const article = articles.find((a) => a.id === articleId);
      if (article && activeArticleMeta?.id !== articleId) {
        setActiveArticleMeta(article);
        setMobileView("article");
      }
    }
  }, [articles, activeArticleMeta?.id]);

  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const articleId = params.get("article");
      if (articleId) {
        const article = articles.find((a) => a.id === articleId);
        if (article) {
          setActiveArticleMeta(article);
          setMobileView("article");
        }
      } else {
        detailRequestRef.current += 1;
        setActiveArticleMeta(null);
        setActiveArticleDetail(null);
        setMobileView("feed");
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [articles]);

  return (
    <div className="h-[100dvh] w-full overflow-hidden bg-white flex dark:bg-zinc-950" data-testid="app-container">
      {mobileView === "feed" && (
        <>
          <button
            className="fixed top-3 left-3 z-50 xl:hidden bg-white dark:bg-zinc-900 border border-[#E5E5E5] dark:border-zinc-800 p-2 rounded-sm focus:outline-none"
            onClick={() => setMobileFeedOpen(!mobileFeedOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Åbn filtre"
          >
            {mobileFeedOpen ? <X className="w-4 h-4" strokeWidth={1.5} /> : <Menu className="w-4 h-4" strokeWidth={1.5} />}
          </button>

          <button
            className="fixed top-3 right-3 z-50 md:hidden bg-white dark:bg-zinc-900 border border-[#E5E5E5] dark:border-zinc-800 p-2 rounded-sm focus:outline-none"
            onClick={() => setMobileView("kort")}
            data-testid="mobile-kort-nyt-btn"
            aria-label="Vis kort nyt"
          >
            <Zap className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </>
      )}

      <div
        className={`fixed xl:static inset-y-0 left-0 z-40 w-72 xl:w-64 transform transition-transform duration-200 ${
          mobileFeedOpen ? "translate-x-0" : "-translate-x-full xl:translate-x-0"
        }`}
      >
        <Sidebar
          stats={stats}
          activeSource={activeSource}
          onSourceChange={(s) => { setActiveSource(s); closeMobile(); }}
          onHome={goBackToFeed}
          error={dataError}
        />
      </div>

      <div
        className={`w-full md:w-[340px] xl:w-[380px] flex-shrink-0 border-r border-[#E5E5E5] dark:border-zinc-800 h-full flex-col min-w-0 ${
          mobileView === "feed" ? "flex" : "hidden md:flex"
        }`}
      >
        <SearchBar value={searchQuery} onChange={handleSearch} mobileInset />
        <div className="md:hidden flex-shrink-0">
          <BreakingNewsBanner items={breakingItems} />
        </div>
        <div className="flex-1 overflow-hidden">
          <NewsFeed
            lastUpdated={lastUpdated}
            articles={filteredArticles}
            activeArticle={activeArticle}
            newArticleIds={newArticleIds}
            onSelectArticle={selectArticle}
            loading={loading}
            theme={theme}
            onToggleTheme={toggleTheme}
          />
        </div>
      </div>

      <div
        className={`flex-1 h-full min-w-0 flex-col bg-white dark:bg-zinc-950 ${
          mobileView === "article" ? "flex" : "hidden md:flex"
        }`}
      >
        <BreakingNewsBanner items={breakingItems} />
        <div className="md:hidden h-14 px-3 border-b border-[#E5E5E5] dark:border-zinc-800 flex items-center justify-between flex-shrink-0">
          <button
            className="p-2 rounded-sm focus:outline-none"
            onClick={goBackToFeed}
            data-testid="mobile-back-to-feed"
            aria-label="Tilbage til feed"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <span className="text-[10px] tracking-[0.2em] uppercase font-mono text-gray-500 dark:text-gray-400">
            Artikel
          </span>
          <button
            className="p-2 rounded-sm focus:outline-none"
            onClick={() => setMobileView("kort")}
            aria-label="Vis kort nyt"
          >
            <Zap className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        <div className="flex-1 min-h-0">
          <ArticleReader article={activeArticle} />
        </div>
      </div>

      <div
        className={`w-full xl:w-[300px] flex-shrink-0 h-full flex-col bg-white dark:bg-zinc-950 ${
          mobileView === "kort" ? "flex" : "hidden xl:flex"
        }`}
      >
        <div className="xl:hidden h-14 px-3 border-b border-[#E5E5E5] dark:border-zinc-800 flex items-center justify-between flex-shrink-0">
          <button
            className="p-2 rounded-sm focus:outline-none"
            onClick={() => setMobileView(activeArticle ? "article" : "feed")}
            aria-label="Tilbage"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <span className="text-[10px] tracking-[0.2em] uppercase font-mono text-gray-500 dark:text-gray-400">
            Kort nyt
          </span>
          <span className="w-8" />
        </div>
        <div className="flex-1 min-h-0">
          <KortNyt items={kortNytItems} loading={kortNytLoading} />
        </div>
      </div>

      {mobileFeedOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-30 xl:hidden"
          onClick={closeMobile}
          aria-hidden="true"
        />
      )}
    </div>
  );
}

export default App;
